import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';
import { ChevronRight, Mail, UserPen, ArrowLeft, Loader2 } from 'lucide-react';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigateToIntakeForm } from '@client/hooks/useNavigateToIntakeForm';

import { z } from 'zod';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { selectMaybeClient, selectSessionAdvisor } from '@/model';
import { WaterlilyClient } from '@/api/waterlily';
import {
  AgentEnteredWaitingCard,
  agentEnteredIntakeApi,
  inviteApi,
  PrefillDateField,
} from '@/features/invite-client';
import { usePilotAgentEnteredIntake } from '@/shared/hooks/use-active-pilot-program';
import { toDateOnlyString } from '@/shared/lib/utils';

type QuestionnaireMode = 'send-to-client' | 'complete-for-client' | 'request-consent';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

/**
 * Completing a questionnaire on a client's behalf is agent-entered by definition, so these two
 * are collected before anything can be opened. The date of birth is what the client re-enters
 * to prove the link reached the right person.
 */
const agentEnteredSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  birthDate: z.date({ required_error: 'Date of birth is required' }),
});

type AgentEnteredFormValues = z.infer<typeof agentEnteredSchema>;


const MODE_OPTIONS = [
  {
    mode: 'send-to-client' as const,
    icon: <Mail className="size-5" />,
    title: 'Send to client',
    description: 'Email the medical questionnaire to the client to complete on their own.',
  },
  {
    mode: 'complete-for-client' as const,
    icon: <UserPen className="size-5" />,
    title: 'Complete on behalf of client',
    description: "Open and fill out the questionnaire yourself using the client's information.",
  },
];

type QuestionnaireDialogVariant = 'default' | 'underwriting' | 'partner-underwriting';

interface QuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: QuestionnaireDialogVariant;
  client?: Client;
  onBack?: () => void;
}

interface QuestionnaireContentProps {
  client: Client;
  variant?: QuestionnaireDialogVariant;
  onBack?: () => void;
  onClose: () => void;
}

export function QuestionnaireContent({
  client,
  variant = 'default',
  onBack,
  onClose,
}: QuestionnaireContentProps) {
  const advisor = useSelector(selectSessionAdvisor);
  const { navigateToIntakeForm, isLoading: isNavigating } = useNavigateToIntakeForm({
    clientId: client.clientId,
  });
  const [mode, setMode] = useState<QuestionnaireMode | null>(null);
  const [isSending, setIsSending] = useState(false);
  const isAgentEntered = !!usePilotAgentEnteredIntake();
  const [agentEnteredIntakeId, setAgentEnteredIntakeId] = useState<string | null>(null);

  const requestConsent = useMutation({
    mutationFn: agentEnteredIntakeApi.createForClient,
    onSuccess: data => {
      setAgentEnteredIntakeId(data.agentEnteredIntakeId);

      if (data.reused) return;

      if (data.status === 'undeliverable') {
        toast.error('We couldn’t email that address', {
          description: 'It has unsubscribed from Waterlily emails. Try another address.',
        });
        return;
      }

      if (data.status === 'send-failed') {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. Try sending it again.',
        });
        return;
      }

      toast.success('Permission request sent', {
        description: `We emailed ${data.sentTo}. You can open the questionnaire once they accept.`,
      });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to send the permission request.',
      );
    },
  });

  const isLoading = isNavigating || isSending || requestConsent.isPending;

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const agentEnteredForm = useForm<AgentEnteredFormValues>({
    resolver: zodResolver(agentEnteredSchema),
    mode: 'onChange',
    defaultValues: { email: client.clientEmail ?? '' },
  });

  function handleSelectMode(selected: QuestionnaireMode) {
    if (selected === 'send-to-client') {
      if (client.clientEmail) {
        sendFormToClient(client.clientEmail);
      } else {
        setMode('send-to-client');
      }
    } else if (isAgentEntered) {
      // Never writes the `@noemail.waterlily.internal` placeholder. Inventing an address so the
      // agent could open the form with no client involvement is precisely what this replaces.
      setMode('request-consent');
    } else {
      completeForClient();
    }
  }

  function handleAgentEnteredSubmit(values: AgentEnteredFormValues) {
    requestConsent.mutate({
      clientId: client.clientId,
      email: values.email.trim(),
      birthDate: toDateOnlyString(values.birthDate),
    });
  }

  async function saveClientEmail(email: string) {
    await WaterlilyClient.put(`/client/${client.clientId}`, {
      ...client,
      clientEmail: email,
    });
  }

  async function sendFormToClient(email: string) {
    setIsSending(true);
    try {
      if (email !== client.clientEmail) {
        await saveClientEmail(email);
      }

      const intakeFormLink = getIntroIntakeFormApiEndPoint(advisor.advisorId, false);

      await inviteApi.sendInvite({
        clientId: client.clientId,
        advisorId: advisor.advisorId,
        email,
        clientFirstName: client.clientFirstName,
        clientLastName: client.clientLastName,
        intakeFormLink,
      });

      onClose();
      toast.success('Questionnaire sent successfully');
    } finally {
      setIsSending(false);
    }
  }

  async function completeForClient() {
    const placeholderEmail = `${client.clientId}@noemail.waterlily.internal`;
    if (!client.clientEmail) {
      await saveClientEmail(placeholderEmail);
    }
    await navigateToIntakeForm();
    onClose();
  }

  function handleEmailSubmit(values: EmailFormValues) {
    sendFormToClient(values.email);
  }

  const { title, description } = getDialogCopy(mode, variant);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      {mode === null && onBack && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={onBack}
        >
          <ArrowLeft />
          Back
        </Button>
      )}

      {mode !== null && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => {
            setMode(null);
            form.reset();
            agentEnteredForm.reset();
          }}
        >
          <ArrowLeft />
          Back
        </Button>
      )}

      {mode === null && (
        <div className="flex flex-col gap-3">
          {MODE_OPTIONS.map(
            ({ mode: optionMode, icon, title: optionTitle, description: optionDesc }) => (
              <button
                key={optionMode}
                type="button"
                disabled={isLoading}
                onClick={() => handleSelectMode(optionMode)}
                className="hover:border-primary hover:bg-primary/5 border-border bg-background group flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-all disabled:pointer-events-none disabled:opacity-50"
              >
                <div className="group-hover:bg-primary text-foreground bg-muted group-hover:text-background flex size-12 shrink-0 items-center justify-center rounded-full transition-all">
                  {icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-950">{optionTitle}</h3>
                  <p className="text-foreground text-sm">{optionDesc}</p>
                </div>
                {isLoading ? (
                  <Loader2 className="text-muted-foreground size-5 animate-spin" />
                ) : (
                  <ChevronRight className="group-hover:text-primary text-muted-foreground size-5 transition-all" />
                )}
              </button>
            ),
          )}
        </div>
      )}

      {mode === 'request-consent' && agentEnteredIntakeId && (
        <AgentEnteredWaitingCard
          requestId={agentEnteredIntakeId}
          onDismiss={() => {
            setAgentEnteredIntakeId(null);
            agentEnteredForm.reset();
          }}
        />
      )}

      {mode === 'request-consent' && !agentEnteredIntakeId && (
        <Form {...agentEnteredForm}>
          <form
            onSubmit={agentEnteredForm.handleSubmit(handleAgentEnteredSubmit)}
            className="space-y-4"
          >
            <FormField
              control={agentEnteredForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Client email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="jane@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={agentEnteredForm.control}
              name="birthDate"
              render={({ field, fieldState }) => (
                <FormItem>
                  <PrefillDateField
                    id="questionnaire-agent-entered-birth-date"
                    label="Client date of birth"
                    value={field.value}
                    error={fieldState.error?.message}
                    onChange={field.onChange}
                  />
                </FormItem>
              )}
            />
            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={!agentEnteredForm.formState.isValid || isLoading}
              >
                {isLoading && <Loader2 className="animate-spin" />}
                Request Client Consent
              </Button>
            </DialogFooter>
          </form>
        </Form>
      )}

      {mode === 'send-to-client' && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleEmailSubmit)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Client email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="jane@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="animate-spin" />}
                Send Questionnaire
              </Button>
            </DialogFooter>
          </form>
        </Form>
      )}
    </>
  );
}

export function QuestionnaireDialog({
  open,
  onOpenChange,
  variant = 'default',
  client: clientProp,
  onBack,
}: QuestionnaireDialogProps) {
  const reduxClient = useSelector(selectMaybeClient);
  const client = clientProp ?? reduxClient!;

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <QuestionnaireContent
          client={client}
          variant={variant}
          onBack={
            onBack
              ? () => {
                  handleOpenChange(false);
                  onBack();
                }
              : undefined
          }
          onClose={() => handleOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function getDialogCopy(mode: QuestionnaireMode | null, variant: QuestionnaireDialogVariant) {
  if (mode === 'request-consent') {
    return {
      title: 'Ask Your Client for Permission',
      description:
        "Confirm your client's email and date of birth. We'll email them for permission before you can complete the questionnaire on their behalf.",
    };
  }

  if (mode === 'send-to-client') {
    return {
      title: 'Send to Client',
      description: "Enter the client's email address to send the medical questionnaire.",
    };
  }

  if (variant === 'underwriting') {
    return {
      title: 'Complete Medical Questionnaire',
      description:
        'Complete the medical questionnaire to enable AI-powered underwriting analysis. Once submitted, eligibility insights will automatically appear on each product.',
    };
  }
  if (variant === 'partner-underwriting') {
    return {
      title: 'Complete Medical Questionnaire For Partner',
      description:
        'Complete the medical questionnaire for the partner to enable AI-powered underwriting analysis. Once submitted, eligibility insights will automatically appear on each product.',
    };
  }

  return {
    title: 'Complete Medical Questionnaire',
    description: "Choose how you'd like to complete the questionnaire.",
  };
}
